import { ContentType } from "./http-client";

export class WorkOrders {
  constructor(http) {
    this.http = http;
  }

  create = (payload) =>
    this.http.request({
      path: "/api/maintenance/work-orders",
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  list = (params = {}) =>
    this.http.request({
      path: "/api/maintenance/work-orders",
      method: "GET",
      query: params,
      secure: true,
    });

  get = (workOrderId) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}`,
      method: "GET",
      secure: true,
    });

  update = (workOrderId, payload) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}`,
      method: "PATCH",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  approve = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/approve`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  reject = (workOrderId, payload = {}) => {
    const notes = payload.notes || payload.reason;
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/reject`,
      method: "POST",
      body: { ...payload, notes, reason: notes },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Assign a work order to a vendor with scheduling details.
   * Transitions status to ASSIGNED.
   * @param {string} workOrderId
   * @param {{ vendorId: string, scheduledStart?: string, scheduledEnd?: string, notes?: string }} payload
   */
  assign = (workOrderId, payload = {}) => {
    const body = typeof payload === "string" ? { vendorId: payload } : payload;
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/assign`,
      method: "POST",
      body: {
        vendorId: body.vendorId || body.vendor_id,
        scheduledStart: body.scheduledStart || body.scheduled_start,
        scheduledEnd: body.scheduledEnd || body.scheduled_end,
        notes: body.notes,
      },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Commence work on a work order.
   * Transitions status to IN_PROGRESS.
   * @param {string} workOrderId
   * @param {{ notes?: string, reason?: string }|string} [payload]
   */
  start = (workOrderId, payload = {}) => {
    const notes = typeof payload === "string" ? payload : (payload.notes || payload.reason);
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/start`,
      method: "POST",
      body: { notes, reason: notes },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Submit completed work order for managerial review.
   * Transitions status to REVIEW.
   * @param {string} workOrderId
   * @param {{ actualCost?: number, notes?: string, reason?: string }} [payload]
   */
  submitReview = (workOrderId, payload = {}) => {
    const notes = typeof payload === "string" ? payload : (payload.notes || payload.reason);
    const rawCost = payload.actualCost ?? payload.actual_cost;
    const actualCost =
      rawCost !== undefined && rawCost !== "" && rawCost !== null
        ? Number(rawCost)
        : undefined;
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/submit-review`,
      method: "POST",
      body: {
        actualCost,
        notes,
        reason: notes,
      },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Verify and complete a work order.
   * Transitions status to COMPLETED.
   * @param {string} workOrderId
   * @param {{ finalCost?: number, actualCost?: number, signOffNotes?: string, notes?: string }} [payload]
   */
  complete = (workOrderId, payload = {}) => {
    const rawCost =
      payload.finalCost ?? payload.actualCost ?? payload.actual_cost;
    const cost =
      rawCost !== undefined && rawCost !== "" && rawCost !== null
        ? Number(rawCost)
        : undefined;
    const notes =
      payload.signOffNotes || payload.notes || payload.reason;
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/complete`,
      method: "POST",
      body: {
        actualCost: cost,
        notes,
        reason: notes,
      },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Cancel an active or pending work order.
   * Transitions status to CANCELLED.
   * @param {string} workOrderId
   * @param {{ reason?: string, notes?: string }|string} [payload]
   */
  cancel = (workOrderId, payload = {}) => {
    const reason =
      typeof payload === "string" ? payload : (payload.reason || payload.notes);
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/cancel`,
      method: "POST",
      body: { reason, notes: reason },
      type: ContentType.Json,
      secure: true,
    });
  };

  /**
   * Request contractor rework on a reviewed work order.
   * Transitions status back to IN_PROGRESS.
   * @param {string} workOrderId
   * @param {{ reworkNotes?: string, reason?: string, notes?: string }|string} [payload]
   */
  rework = (workOrderId, payload = {}) => {
    const reworkNotes =
      typeof payload === "string"
        ? payload
        : (payload.reworkNotes || payload.reason || payload.notes);
    return this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/rework`,
      method: "POST",
      body: { reason: reworkNotes, notes: reworkNotes },
      type: ContentType.Json,
      secure: true,
    });
  };

  enRoute = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/en-route`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  onSite = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/on-site`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  invoice = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/invoice`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  dispatch = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/dispatch`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  cancelDispatch = (workOrderId, dispatchId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/dispatch/${dispatchId}/cancel`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });

  attachToMoveOut = (workOrderId, payload = {}) =>
    this.http.request({
      path: `/api/maintenance/work-orders/${workOrderId}/attach-to-move-out`,
      method: "POST",
      body: payload,
      type: ContentType.Json,
      secure: true,
    });
}
